import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store, select } from '@ngrx/store';
import { concatMap, filter, map, take } from 'rxjs/operators';

import { NewsletterService } from 'ish-core/services/newsletter/newsletter.service';
import { displayErrorMessage, displaySuccessMessage } from 'ish-core/store/core/messages';
import { mapErrorToAction, mapToPayload, mapToPayloadProperty, whenTruthy } from 'ish-core/utils/operators';

import { userNewsletterActions, userNewsletterApiActions } from './user.actions';
import { getLoggedInUser } from './user.selectors';

@Injectable()
export class UserNewsletterEffects {
  constructor(private actions$: Actions, private store: Store, private newsletterService: NewsletterService) {}

  /**
   * The effect has to wait for the getLoggedInUser-selector because it is used in a page-guard that is called before
   * the user is ready in the store
   */
  loadUserNewsletterSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userNewsletterActions.loadUserNewsletterSubscription),
      concatMap(() =>
        this.store.pipe(
          select(getLoggedInUser),
          whenTruthy(),
          take(1),
          concatMap(user =>
            this.newsletterService.getSubscription(user.email).pipe(
              map(subscriptionStatus =>
                userNewsletterApiActions.loadUserNewsletterSubscriptionSuccess({ subscribed: subscriptionStatus })
              ),
              mapErrorToAction(userNewsletterApiActions.loadUserNewsletterSubscriptionFail)
            )
          )
        )
      )
    )
  );

  /**
   * The user-email has to be provided when setting the subscription during the registration process
   * because the user is not logged in yet.
   * If no user-email is passed in, the email of the logged-in user is used.
   */
  updateNewsletterSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userNewsletterActions.updateUserNewsletterSubscription),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInUser))),
      filter(([payload, user]) => !!payload.userEmail || !!user?.email),
      concatMap(([payload, user]) =>
        this.newsletterService
          .updateNewsletterSubscriptionStatus(
            payload.subscriptionStatus,
            payload.userEmail || user.email,
            payload.isAnonymousUser
          )
          .pipe(
            concatMap(subscriptionStatus => {
              const actions: Action[] = [
                userNewsletterApiActions.updateUserNewsletterSubscriptionSuccess({ subscriptionStatus }),
              ];

              // 👇 only show success message if flag is true (default true if not passed)
              if (payload.showMessage !== false) {
                actions.push(
                  displaySuccessMessage({
                    message: 'Subscription successful',
                  })
                );
              }

              return actions;
            }),
            mapErrorToAction(userNewsletterApiActions.updateUserNewsletterSubscriptionFail)
          )
      )
    )
  );

  displayPunchoutErrorMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userNewsletterApiActions.updateUserNewsletterSubscriptionFail),
      mapToPayloadProperty('error'),
      map(() =>
        displayErrorMessage({
          message: 'Subscription failed',
        })
      )
    )
  );
}
